import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetSystemPackages,
  CreateSystemPackage,
  UpdateSystemPackage,
  DeleteSystemPackage,
  GetSystemPackageSubFeatures,
  UpdateSystemPackageSubFeature } from "../Api/system_package_api";
import { unwrapApiResponse } from "./query_helpers";
import { ApiResponse } from "../Types";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

const PACKAGES_QUERY_KEY = ["system-packages"];

export const useSystemPackagesQuery = () =>
  useQuery({
    queryKey: PACKAGES_QUERY_KEY,
    queryFn: async () => unwrapApiResponse(await GetSystemPackages()),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useSystemPackageSubFeaturesQuery = (id: string | undefined) =>
  useQuery({
    queryKey: [...PACKAGES_QUERY_KEY, id, "sub-features"],
    queryFn: async () =>
      unwrapApiResponse(await GetSystemPackageSubFeatures(id!)),
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useCreateSystemPackageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; price: number; note?: string }) =>
      CreateSystemPackage(payload),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: PACKAGES_QUERY_KEY });
      }
    },
  });
};

export const useUpdateSystemPackageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      id: string;
      name?: string;
      price?: number;
      note?: string;
      status?: string;
    }) => UpdateSystemPackage(payload),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: PACKAGES_QUERY_KEY });
      }
    },
  });
};

export const useDeleteSystemPackageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => DeleteSystemPackage(id),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        queryClient.invalidateQueries({ queryKey: PACKAGES_QUERY_KEY });
      }
    },
  });
};

export const useUpdateSystemPackageSubFeatureMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      package_id: string;
      sub_feature_id: string;
      is_enable: number;
    }) => UpdateSystemPackageSubFeature(payload),
    onSuccess: (
      res: ApiResponse,
      variables: {
        package_id: string;
        sub_feature_id: string;
        is_enable: number;
      },
    ) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [
            ...PACKAGES_QUERY_KEY,
            variables.package_id,
            "sub-features",
          ],
        });
      }
    },
  });
};
